import Icon from '@/components/snippets/icon/icon'
import clsx from 'clsx'
import { useEffect, useState } from 'preact/hooks'
import { globalState } from '@/components/helpers/utils'
import { generateSrcSet } from './ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { render } from 'preact'
import { QuickAddSwatches } from 'libs/searchspring/components/ResultSwatches'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'

const IMAGE_WIDTHS = [320, 480, 560, 640, 720, 960]

const getStockStatus = (variant, stockLevels) => {
  // If variant doesn't exist, return out-of-stock status
  if (!variant) return { inStock: false, text: '' }

  // Actual inventory quantity
  const quantity = variant.inventory_quantity

  // Check if there's a specific message for quantity 0
  const zeroStockLevel = stockLevels.find((stock) => stock.max_quantity === 0)

  // If quantity is 0 and we have a specific message for it, use that message
  if (quantity <= 0 && zeroStockLevel) {
    return {
      inStock: false,
      text: zeroStockLevel.message,
      warning: true,
      stockLevel: quantity,
      textColour: zeroStockLevel.message_colour
    }
  }

  // If quantity is 0 but no specific message, use default out of stock message
  if (quantity <= 0) {
    return { inStock: false, text: 'Out of stock', warning: true, stockLevel: quantity, textColour: '#b3b3b3' }
  }

  // Check against thresholds (sorted from highest to lowest)
  for (const stock of stockLevels) {
    if (quantity <= stock.max_quantity) {
      return {
        inStock: true,
        text: stock.message,
        warning: true,
        stockLevel: quantity,
        textColour: stock.message_colour
      }
    }
  }

  // If no threshold is matched, item is fully in stock
  return {
    inStock: true,
    text: '',
    warning: false,
    stockLevel: quantity,
    text_color: ''
  }
}

const ActionButtons = ({ currentResult, setSelectedSize, selectedSize, preRelease, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [targetVariant, setTargetVariant] = useState(null)
  const [errorMessage, setErrorMessage] = useState(false)
  const [buttonText, setButtonText] = useState('Add To Cart')

  useEffect(() => {
    setTargetVariant(currentResult.variants.find((v) => v.option1 === selectedSize))
    if (preRelease) {
      return setButtonText('Coming soon')
    }
    if (!selectedSize || selectedSize === '') {
      return setButtonText('Select Size')
    }

    setButtonText('Add To Cart')
  }, [selectedSize])

  const handleAddToCart = async () => {
    if (!targetVariant) return

    if (window.Cart) {
      setLoading(true)
      try {
        await window.Cart.addLineItems({
          id: targetVariant.id,
          quantity: 1
        })
        setSelectedSize('')
        // Close modal after successful add to cart
        onClose()
      } catch (error) {
        console.error('Error adding item to cart:', error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleSubmit = () => {
    if (selectedSize && currentResult && preRelease === false) {
      handleAddToCart()
    }
    if (!selectedSize || selectedSize === '') {
      setErrorMessage(true)
    }
  }
  return (
    <div className="flex shrink-0 flex-col gap-y-0.5 bg-neutral-light px-1 py-1.25 md:px-1.5 md:py-1.5">
      {errorMessage && !selectedSize && (
        <div className='bg-system-error-light text-system-error mt-0.5 py-0.75 px-1'>
          <p>Please select a size to add to cart</p>
        </div>
      )}
      <button
        className={clsx(
          'btn w-full shrink-0 btn-primary btn-m',
          !selectedSize ? 'disabled btn-border' : 'btn-fill',
          loading && ' disabled',
          preRelease && ' disabled'
        )}
        disabled={preRelease}
        type="button"
        onClick={handleSubmit}
        aria-label={`${buttonText}`}
      >
        {buttonText}
      </button>
      <a href={currentResult.url} className="btn w-full btn-text btn-m">
        <span className="font-line capitalize">View Product Details</span>
      </a>
    </div>
  )
}

const ProgressBar = ({ currentIndex, totalItems }) => {
  const progressPercentage = totalItems > 1 ? ((currentIndex + 1) / totalItems) * 100 : 100

  return (
    <div className="absolute left-0 bottom-0 w-full">
      <div className="w-full h-[2px] bg-transparent overflow-hidden">
        <div
          className="h-full bg-brand-black transition-all duration-300 ease-out"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>
    </div>
  )
}

// Modal Content Component - This will be rendered into the container
const ModalContent = ({ result, modalId, firstPromoline, secondPromoline, sizeGuide, onClose }) => {
  const { currentResult, updateSwatch } = useSwatches(result)
  const [selectedSize, setSelectedSize] = useState('')
  const [preRelease, setPreRelease] = useState(false)
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0)
  const availabilityStatus = currentResult?.mfield_custom_availability_status || ''

  // Set up carousel tracking when modal opens
  useEffect(() => {
    const setupCarousel = () => {
      const carousel = document.querySelector('.quick-add-carousel')
      if (carousel && carousel.flickity) {
        setCurrentCarouselIndex(0)

        const handleCarouselChange = () => {
          setCurrentCarouselIndex(carousel.flickity.selectedIndex)
        }

        carousel.flickity.on('change', handleCarouselChange)
        return () => {
          if (carousel.flickity) {
            carousel.flickity.off('change', handleCarouselChange)
          }
        }
      }
    }

    const timeoutId = setTimeout(setupCarousel, 100)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [])

  if (availabilityStatus === 'Coming Soon') {
    const releaseDate = currentResult.mfield_custom_release_date || ''
    const dateTime = Math.floor(Date.now() / 1000) // Current timestamp in seconds

    if (releaseDate != null && releaseDate !== '') {
      const endTime = Math.floor(new Date(releaseDate).getTime() / 1000) // Convert to timestamp in seconds
      if (endTime > dateTime) {
        setPreRelease(true)
      }
    } else {
      setPreRelease(true)
    }
  }

  return (
    <>
      {/* Modal Header */}
      <div className="sticky top-0 z-20 flex h-[52px] shrink-0 items-center justify-between border-b border-neutral-light px-1 md:h-[60px] md:px-1.5">
        <h4 className="h4">Quick Add</h4>
        <button
          className="size-[13px] link-fade children:size-full"
          data-modal-parent={modalId}
          data-modal-role="close"
          aria-label="Close quick add modal"
          onClick={onClose}
        >
          <Icon id={'close'} />
        </button>
      </div>
      {/* Modal Inner */}
      <div className="flex grow flex-col gap-y-1.5 overflow-y-auto overscroll-none children:shrink-0">
        {/* Product Images Carousel */}
        {result?.images?.length > 0 && (
          <>
            <div className='overflow-hidden w-full relative'>
              <x-carousel
                className='flex w-full children:shrink-0 quick-add-carousel'
                carousel-settings='{
                  "draggable": true,
                  "freeScroll": false,
                  "wrapAround": false,
                  "groupCells": false,
                  "fade": false,
                  "adaptiveHeight": false,
                  "accessibility": true,
                  "cellAlign": "left",
                  "prevNextButtons": false,
                  "pageDots": false
                }'
              >
                {currentResult.images.map((image, index) => (
                  <div key={index} className='overflow-hidden mr-0.25 aspect-2/3 w-[44.44%] max-w-[250px]'>
                    <img
                      className='h-full w-full object-cover'
                      src={image.src}
                      srcSet={generateSrcSet(image.src, IMAGE_WIDTHS)}
                      sizes='50vw'
                      alt={image.alt || result.title || `Product image ${index + 1}`}
                    />
                  </div>
                ))}
              </x-carousel>
              {currentResult.images.length > 1 && (
                <ProgressBar currentIndex={currentCarouselIndex} totalItems={currentResult.images.length} />
              )}
            </div>
          </>
        )}
        {/* Product Details */}
        <div className="flex flex-col gap-y-1 px-1 pb-2.5 md:px-1.5">
          <h3 className="h3 capitalize" dangerouslySetInnerHTML={{ __html: currentResult.title }}></h3>
          <Price price={result.ss_price} compareAtPrice={result.ss_retail} className="!paragraph-l flex gap-x-0.25" />
          {(firstPromoline || secondPromoline) && (
            <div className="flex flex-col gap-0.25">
              {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="Quick Add" promolineSize="large" />}
              {secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="Quick Add" promolineSize="large" />}
            </div>
          )}
          <hr className="border-neutral-medium" />
          <QuickAddSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />
          {/* Size Selector */}
          <SizeSelector
            result={currentResult}
            variants={currentResult.variants}
            setSelectedSize={setSelectedSize}
            selectedSize={selectedSize}
            sizeGuide={sizeGuide}
            preRelease={preRelease}
          />
        </div>
      </div>
      <ActionButtons
        currentResult={currentResult}
        setSelectedSize={setSelectedSize}
        selectedSize={selectedSize}
        preRelease={preRelease}
        onClose={onClose}
      />
    </>
  )
}

const SizeSelector = ({ result, variants, setSelectedSize, selectedSize, sizeGuide, preRelease }) => {
  const stockLevels = globalState?.metaobjects.stock_indicators?.sort((a, b) => b.max_quantity - a.max_quantity) || []
  const getStatusForSize = (size) => {
    const variant = variants.find((v) => v.option1 === size)
    return getStockStatus(variant, stockLevels)
  }

  const handleSizeGuideClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (sizeGuide?.id) {
      const modalId = `size-modal-${sizeGuide.id}`
      const modalElement = document.querySelector(`x-modal[data-id="${modalId}"]`)
      if (modalElement) {
        const openButton = modalElement.querySelector('[data-modal-role="open"]')
        if (openButton) {
          openButton.click()
        } else {
          const modalInstance = modalElement
          if (modalInstance && typeof modalInstance.handleShow === 'function') {
            modalInstance.handleShow()
          }
        }
      }
    }
  }

  if (variants.length <= 1) {
    setSelectedSize(variants[0].option1)
    return
  }

  return (
    <div className="w-full">
      <div className="flex flex-col">
        <div className="relative flex items-center justify-between gap-x-1 pb-1">
          <span className="overflow-hidden paragraph-s text-ellipsis whitespace-nowrap text-s">
            <span className="font-medium">{result.is_giftcard !== 'true' ? 'Size' : 'Value'}:</span>{' '}
            {selectedSize ? `${selectedSize}` : 'Select'}
          </span>
          {sizeGuide && sizeGuide.tables.length > 0 && (
            <button
              type="button"
              className="paragraph-xs !capitalize text-brand-black underline underline-offset-4"
              onClick={handleSizeGuideClick}
              aria-label="View Size Guide"
            >
              Size Guide
            </button>
          )}
        </div>

        <div className="relative flex flex-wrap gap-x-0.5 gap-y-0.25">
          {variants.map((variant) => {
            const status = getStatusForSize(variant.option1)
            return (
              <div
                key={variant.id}
                role="option"
                aria-selected={selectedSize === variant.option1}
                tabIndex={status.inStock ? 0 : -1}
                className={`
                  group/option btn h-[44px] w-[50px] max-w-grid-1/4 grow !px-0 text-xs btn-m
                  ${(status.inStock && preRelease === false) ? 'btn-border btn-primary btn-primary-alt' : 'btn-fill btn-light btn-light-alt'}
                  ${selectedSize === variant.option1 ? 'active' : ''}
                `}
                disabled={!status.inStock || preRelease}
                onClick={() => {
                  if (status.inStock && preRelease === false) {
                    setSelectedSize(variant.option1)
                  }
                }}
                aria-disabled={!status.inStock || preRelease}
              >
                <span className="text-xs font-medium">{variant.option1}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export function ResultQuickAdd({ result, firstPromoline, secondPromoline, sizeGuide }) {
  const buttonClass =
    'btn btn-primary btn-transparent lg:btn-fill lg-max:btn-square lg:btn-s w-[20px] lg:w-full lg-max:!px-0 lg:opacity-0 group-hover/card:opacity-100'

  const closeModal = (modalId) => {
    const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
    if (modal && typeof modal.handleHide === 'function') {
      modal.handleHide()
    }
  }

  const openModal = (modalId) => {
    setTimeout(() => {
      const modal = document.querySelector(`x-modal[data-id="${modalId}"]`)
      if (modal && typeof modal.handleToggle === 'function') {
        modal.handleToggle()
      }
    }, 100)
  }

  const handleModalOpen = () => {
    const modalId = 'quick-add-modal'
    const quickAddContainer = document.getElementById('quick-add-modal-inner')
    if (quickAddContainer) {
      render(null, quickAddContainer)
      render(
        <ModalContent
          key={`modal-${result.handle || result.id}-${Date.now()}`}
          result={result}
          firstPromoline={firstPromoline}
          secondPromoline={secondPromoline}
          sizeGuide={sizeGuide}
          onClose={() => closeModal(modalId)}
        />,
        quickAddContainer
      )
    }
    openModal(modalId)
  }

  // For multiple variant products, render a button that opens quick add modal with additional info
  return (
    <button className={buttonClass} type="button" onClick={handleModalOpen} aria-label="Open quick add modal">
      <span className="lg-max:hidden font-medium">Quick View</span>
    </button>
  )
}
